import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Instagram, X } from "lucide-react";
import {
  getGalleryPhotos,
  INSTAGRAM_PROFILE_URL,
} from "@/config/instagramGallery";
import { trapTabKey } from "@/lib/focusTrap";

const SWIPE_THRESHOLD = 50;

export default function Gallery({ t }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const touchStartX = useRef(null);
  const openerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const galleryPhotos = useMemo(() => getGalleryPhotos(), []);

  const activePhoto = activeIndex === null ? null : galleryPhotos[activeIndex];
  const isOpen = activeIndex !== null;
  const isHungarian = t.gallery.kicker === "Galéria";
  const previousLabel = isHungarian ? "Előző kép" : "Previous image";
  const nextLabel = isHungarian ? "Következő kép" : "Next image";
  const instagramLabel = isHungarian
    ? "Még több pillanat Instagramon"
    : "More moments on Instagram";
  const gallerySub = isHungarian
    ? "Pillanatok, amelyekben a Telefonka is a történet részévé vált."
    : "Moments where Telefonka became part of the story.";

  const getAlt = useCallback(
    (photo) => (isHungarian ? photo.altHu || photo.altEn : photo.altEn || photo.altHu),
    [isHungarian],
  );

  const showPrevious = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return null;
      return (current - 1 + galleryPhotos.length) % galleryPhotos.length;
    });
  }, [galleryPhotos.length]);

  const showNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return null;
      return (current + 1) % galleryPhotos.length;
    });
  }, [galleryPhotos.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPrevious();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNext();
      } else if (event.key === "Escape") {
        event.preventDefault();
        setActiveIndex(null);
      } else {
        trapTabKey(event, dialogRef.current);
      }
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, showNext, showPrevious]);

  useEffect(() => {
    if (!isOpen) openerRef.current?.focus();
  }, [isOpen]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const endX = event.changedTouches[0]?.clientX;
    if (typeof endX !== "number") {
      touchStartX.current = null;
      return;
    }

    const distance = endX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(distance) < SWIPE_THRESHOLD) return;

    if (distance < 0) {
      showNext();
    } else {
      showPrevious();
    }
  };

  return (
    <section
      id="gallery"
      data-testid="section-gallery"
      className="py-24 md:py-32 bg-[var(--beige-100)] border-y border-[rgba(110,30,43,0.07)] tk-section-anchor"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="text-center mb-14 md:mb-16 max-w-2xl mx-auto">
          <span className="tk-eyebrow">
            <span className="tk-divider" />
            {t.gallery.kicker}
            <span className="tk-divider" />
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[var(--ink)]">
            {t.gallery.title}
          </h2>
          <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{gallerySub}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9 items-stretch">
          {galleryPhotos.map((photo, index) => {
            const alt = getAlt(photo);

            return (
              <button
                type="button"
                key={`${photo.src}-${index}`}
                onClick={(event) => {
                  openerRef.current = event.currentTarget;
                  setActiveIndex(index);
                }}
                data-testid={`gallery-image-${index + 1}`}
                className="group relative overflow-hidden rounded-[22px] bg-white aspect-[4/5] shadow-[0_14px_35px_-24px_rgba(44,31,26,0.28)] border border-[rgba(110,30,43,0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--burgundy)]"
                aria-label={alt}
              >
                <img
                  src={photo.src}
                  alt={alt}
                  width={photo.width}
                  height={photo.height}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.015]"
                  style={{ objectPosition: photo.objectPosition || "center center" }}
                  loading="lazy"
                  decoding="async"
                />
                <span
                  className="absolute inset-0 bg-[rgba(110,30,43,0)] transition-colors duration-300 group-hover:bg-[rgba(110,30,43,0.035)]"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <div className="mt-10 md:mt-12 flex justify-center">
          <a
            href={INSTAGRAM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm text-[var(--ink-soft)] transition hover:text-[var(--burgundy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
            aria-label={`${instagramLabel} – @hellotelefonka`}
          >
            <Instagram size={16} aria-hidden="true" />
            <span>{instagramLabel}</span>
          </a>
        </div>
      </div>

      {activePhoto && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="tk-dialog-focus-target fixed inset-0 z-50 bg-[rgba(44,31,26,0.84)] backdrop-blur-[2px] p-4 sm:p-5 md:p-8 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={getAlt(activePhoto)}
          onClick={() => setActiveIndex(null)}
        >
          <div
            className="relative w-full max-w-6xl flex items-center justify-center"
            onClick={(event) => event.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <button
              type="button"
              onClick={showPrevious}
              className="absolute left-1 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[var(--beige-50)]/95 flex items-center justify-center text-[var(--burgundy)] shadow-lg border border-white/50 transition hover:bg-white hover:scale-[1.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label={previousLabel}
            >
              <ChevronLeft size={22} aria-hidden="true" />
            </button>

            <div className="relative max-w-5xl w-full max-h-[88vh] max-h-[88dvh] overflow-hidden rounded-3xl bg-[var(--ink)] shadow-2xl mx-8 sm:mx-12 md:mx-16">
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setActiveIndex(null)}
                className="absolute top-4 right-4 z-20 w-11 h-11 rounded-full bg-white/95 flex items-center justify-center text-[var(--ink)] shadow-lg hover:bg-white transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                aria-label={t.gallery.close}
              >
                <X size={20} aria-hidden="true" />
              </button>

              <img
                key={activePhoto.src}
                src={activePhoto.src}
                alt={getAlt(activePhoto)}
                width={activePhoto.width}
                height={activePhoto.height}
                loading="eager"
                className="block max-h-[88vh] max-h-[88dvh] w-full object-contain"
                style={{ animation: "tkFade 0.28s ease both" }}
                decoding="async"
              />
            </div>

            <button
              type="button"
              onClick={showNext}
              className="absolute right-1 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[var(--beige-50)]/95 flex items-center justify-center text-[var(--burgundy)] shadow-lg border border-white/50 transition hover:bg-white hover:scale-[1.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label={nextLabel}
            >
              <ChevronRight size={22} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
